import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { Output as OutputIcon } from '@mui/icons-material';

interface OutputNodeData {
  id: number;
  label: string;
  tipo: string;
  color: string;
  porta_numero?: number;
  status?: 'available' | 'occupied' | 'disabled';
  cliente_nome?: string;
}

const OutputNode: React.FC<NodeProps<OutputNodeData>> = ({ data, selected }) => {
  const handleColor = data.color || '#FF9800';
  
  // Cores baseadas no status
  const getStatusColor = () => {
    switch (data.status) {
      case 'occupied':
        return '#f44336'; // Vermelho para ocupada
      case 'disabled':
        return '#9e9e9e'; // Cinza para desabilitada
      case 'available':
      default:
        return '#4caf50'; // Verde para disponível
    }
  };

  const getStatusLabel = () => {
    switch (data.status) {
      case 'occupied':
        return 'OCUPADA';
      case 'disabled':
        return 'INATIVA';
      case 'available':
      default:
        return 'LIVRE';
    }
  };
  
  const statusColor = getStatusColor();
  
  return (
    <Paper
      elevation={selected ? 8 : 3}
      sx={{
        minWidth: 120,
        minHeight: 60,
        border: selected ? `2px solid ${handleColor}` : '1px solid #ddd',
        borderRadius: 2,
        backgroundColor: '#fff',
        transition: 'all 0.2s ease',
        position: 'relative',
        '&:hover': {
          elevation: 6,
          transform: 'scale(1.02)'
        }
      }}
    >
      {/* Handle de entrada (esquerda) */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        style={{
          background: handleColor,
          border: '2px solid #fff',
          width: 12,
          height: 12,
          left: -6
        }}
      />
      
      {/* Conteúdo do nó */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        {/* Ícone e tipo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <OutputIcon sx={{ fontSize: 18, color: handleColor }} />
          <Chip
            label="SAÍDA"
            size="small"
            sx={{
              backgroundColor: handleColor,
              color: 'white',
              fontSize: '0.65rem',
              height: 18
            }}
          />
        </Box>
        
        {/* Label principal */}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: '0.75rem',
            color: '#333',
            lineHeight: 1.2
          }}
        >
          {data.label}
        </Typography>
        
        {/* Status da porta */}
        {data.status && (
          <Chip
            label={getStatusLabel()}
            size="small"
            sx={{
              backgroundColor: statusColor,
              color: 'white',
              fontSize: '0.6rem',
              height: 16
            }}
          />
        )}
        
        {/* Número da porta */}
        {data.porta_numero && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.65rem',
              color: '#666',
              textAlign: 'center'
            }}
          >
            Porta {data.porta_numero}
          </Typography>
        )}
        
        {/* Nome do cliente (se ocupada) */}
        {data.cliente_nome && data.status === 'occupied' && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6rem',
              color: '#333',
              textAlign: 'center',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 'bold'
            }}
          >
            {data.cliente_nome}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default memo(OutputNode); 